import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { 
  Bell, Plus, Edit, Trash2, Phone, Mail, Clock, Car, Users,
  Search, CheckCircle2, Unlink, MessageSquare, XCircle, ShieldOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { DAYS, fetchVehicles, getVehicleAssignments } from '@/data/api';
import { useExternalContacts } from '@/contexts/ExternalContactsContext';
import { usePermissions } from '@/hooks/usePermissions';
import { api, getApiErrorMessage, resourceContactRuleGroupEndpoints } from '@/lib/api';
import { PERMISSIONS } from '@/lib/permissions';

const DAY_KEY_TO_ID = {
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
  sun: 7,
};

const DAY_ID_TO_KEY = Object.entries(DAY_KEY_TO_ID).reduce((acc, [key, id]) => ({ ...acc, [id]: key }), {});

const CHANNEL_META = {
  sms: { label: 'SMS', Icon: MessageSquare, color: '#3b82f6', bg: '#dbeafe' },
  email: { label: 'Email', Icon: Mail, color: '#8b5cf6', bg: '#ede9fe' },
  call: { label: 'Chamada', Icon: Phone, color: '#ef4444', bg: '#fee2e2' },
};

const emptyChannel = () => ({
  enabled: true,
  mode: 'uniform',
  days: ['mon', 'tue', 'wed', 'thu', 'fri'],
  start: '08:00',
  end: '18:00',
  perDay: {},
});

const normalizeChannels = (source) => {
  const parsed = typeof source === 'string' ? (() => {
    try { return JSON.parse(source); } catch { return {}; }
  })() : (source || {});

  return {
    sms: { ...emptyChannel(), ...(parsed.sms || {}) },
    email: { ...emptyChannel(), ...(parsed.email || {}) },
    call: { ...emptyChannel(), ...(parsed.call || {}), enabled: parsed.call?.enabled ?? false },
  };
};

const cloneChannels = (channels) => JSON.parse(JSON.stringify(normalizeChannels(channels)));

const timeToHourMinute = (value, fallback = '') => {
  if (!value) return fallback;
  const match = String(value).match(/^(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : String(value);
};

const collectionPayload = (payload, key) => {
  const source = payload?.data ?? payload;
  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.data)) return source.data;
  if (Array.isArray(source?.[key])) return source[key];
  return [];
};

const normalizeExternalContact = (source = {}, fallback = {}) => ({
  ...fallback,
  ...source,
  id: source.id ?? source.external_contact_id ?? source.gest_external_contact_id ?? fallback.id,
  externalContactId: source.gest_external_contact_id ?? source.external_contact_id ?? source.externalContactId ?? source.contact_id ?? source.id ?? fallback.externalContactId ?? fallback.id,
  name: source.name ?? fallback.name ?? '',
  phone: source.phone ?? source.phone_number ?? fallback.phone ?? '',
  email: source.email ?? fallback.email ?? '',
});

const normalizeGroupContact = (source = {}, externalContacts = [], index = 0) => {
  const contactSource = source.external_contact ?? source.contact ?? source;
  const contactId = source.gest_external_contact_id ?? source.external_contact_id ?? source.contact_id ??
    contactSource.gest_external_contact_id ?? contactSource.external_contact_id ?? contactSource.contact_id ??
    contactSource.id ?? source.id ?? null;
  const externalContact = externalContacts.find(contact => String(contact.id) === String(contactId));
  return normalizeExternalContact(
    { ...contactSource, id: contactId, externalContactId: contactId },
    externalContact || { id: contactId ?? index + 1, externalContactId: contactId }
  );
};

const getExternalContactId = (contact = {}) =>
  contact.externalContactId ?? contact.gest_external_contact_id ?? contact.external_contact_id ?? contact.contact_id ?? contact.id;
const numericExternalContactIds = (contacts = []) => contacts
  .map(contact => Number(getExternalContactId(contact)))
  .filter(id => Number.isInteger(id) && id > 0);

const booleanValue = (value) => value === true || value === 1 || value === '1';

const normalizeGroupResourceAssignment = (source = {}, ruleGroupId = null) => ({
  id: source.id ?? source.resource_id ?? source.uuid ?? source.reference,
  reference: source.reference ?? '',
  name: source.name ?? source.title ?? source.reference ?? '',
  ruleGroupId,
  ruleGroupIds: ruleGroupId ? [ruleGroupId] : [],
  hasDriver: Boolean(source.driver) || Boolean(source.company_manager) || booleanValue(source.has_manager) || booleanValue(source.hasDriver),
});

const normalizeChannelDayKey = (day) => {
  const dayId = typeof day === 'object' ? day.day_id ?? day.id : day;
  return DAY_ID_TO_KEY[Number(dayId)] || day?.key || day;
};

const normalizeRuleGroupChannel = (source = {}) => {
  const channel = { ...emptyChannel(), enabled: source.status === undefined ? true : Number(source.status) === 1 };
  const daySource = source.days ?? source.channel_days ?? source.day_ids ?? [];
  const perDaySource = source.per_day ?? source.channel_per_day ?? source.per_days ?? [];
  const days = Array.isArray(daySource)
    ? daySource.map(normalizeChannelDayKey).filter(Boolean)
    : channel.days;
  const perDay = Array.isArray(perDaySource)
    ? perDaySource.reduce((acc, item) => {
        const key = normalizeChannelDayKey(item);
        if (!key) return acc;
        acc[key] = {
          enabled: item.status === undefined ? true : Number(item.status) === 1,
          start: timeToHourMinute(item.start_time_at ?? item.start, '08:00'),
          end: timeToHourMinute(item.end_time_at ?? item.end, '18:00'),
        };
        return acc;
      }, {})
    : {};

  return {
    ...channel,
    mode: source.mode ?? channel.mode,
    days,
    start: timeToHourMinute(source.uniform_start_at ?? source.start, channel.start),
    end: timeToHourMinute(source.uniform_end_at ?? source.end, channel.end),
    perDay,
  };
};

const normalizeRuleGroupChannels = (source) => {
  if (!Array.isArray(source)) return normalizeChannels(source);
  return normalizeChannels(source.reduce((acc, channel) => {
    acc[channel.channel] = normalizeRuleGroupChannel(channel);
    return acc;
  }, {}));
};

const normalizeRuleGroup = (source = {}, externalContacts = [], fallback = {}) => {
  const contacts = collectionPayload(source, 'external_contacts').length
    ? collectionPayload(source, 'external_contacts')
    : collectionPayload(source, 'contacts').length
      ? collectionPayload(source, 'contacts')
      : (fallback.contacts || []);
  const resources = collectionPayload(source, 'resources').length
    ? collectionPayload(source, 'resources')
    : collectionPayload(source, 'assigned_resources');
  const id = source.id ?? source.resource_contact_rule_group_id ?? fallback.id;

  return {
    ...fallback,
    ...source,
    id,
    uuid: source.uuid ?? fallback.uuid,
    name: source.name ?? fallback.name ?? '',
    description: source.description ?? fallback.description ?? '',
    contacts: contacts.map((contact, index) => normalizeGroupContact(contact, externalContacts, index)).filter(contact => contact.id),
    assignments: resources.map(resource => normalizeGroupResourceAssignment(resource, id)).filter(resource => resource.reference || resource.name),
    channels: normalizeRuleGroupChannels(source.channels ?? source.contact_channels ?? fallback.channels),
  };
};

const normalizeRuleGroups = (payload, externalContacts = []) => {
  const groups = collectionPayload(payload, 'resource_contact_rule_groups');
  const fallbackGroups = groups.length ? groups : collectionPayload(payload, 'contact_rule_groups');
  const items = fallbackGroups.length ? fallbackGroups : collectionPayload(payload, 'groups');
  return items.map((group, index) => normalizeRuleGroup(group, externalContacts, { id: index + 1 }));
};

const channelPayload = ([channel, config]) => ({
  channel,
  mode: config.mode,
  uniform_start_at: config.mode === 'uniform' ? timeToHourMinute(config.start) : null,
  uniform_end_at: config.mode === 'uniform' ? timeToHourMinute(config.end) : null,
  status: config.enabled ? 1 : 0,
  day_ids: config.days.map(day => DAY_KEY_TO_ID[day]).filter(Boolean),
  per_day: config.days.map(day => ({
    day_id: DAY_KEY_TO_ID[day],
    start_time_at: timeToHourMinute(config.perDay?.[day]?.start ?? config.start),
    end_time_at: timeToHourMinute(config.perDay?.[day]?.end ?? config.end),
    status: config.mode === 'per-day' ? (config.perDay?.[day]?.enabled ? 1 : 0) : 1,
  })).filter(day => day.day_id),
});

const ruleGroupPayload = ({ name, description, contacts, channels }) => ({
  name: name.trim(),
  description: description.trim() || null,
  external_contact_ids: numericExternalContactIds(contacts),
  gest_external_contact_ids: numericExternalContactIds(contacts),
  channels: Object.entries(normalizeChannels(channels)).map(channelPayload),
});

const formatDaysShort = (days = []) => {
  if (days.length === 7) return 'Todos os dias';
  if (days.length === 5 && ['mon', 'tue', 'wed', 'thu', 'fri'].every(d => days.includes(d))) return 'Dias úteis';
  if (days.length === 2 && days.includes('sat') && days.includes('sun')) return 'Fins-de-semana';
  return days.map(d => DAYS.find(x => x.key === d)?.short).filter(Boolean).join(', ');
};

const ChannelEditor = ({ channelKey, channel, onChange }) => {
  const meta = CHANNEL_META[channelKey];
  const Icon = meta.Icon;

  const toggleDay = (dayKey) => {
    const days = channel.days.includes(dayKey)
      ? channel.days.filter(current => current !== dayKey)
      : [...channel.days, dayKey];
    onChange({ ...channel, days });
  };

  const setDaysPreset = (preset) => {
    const all = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const weekdays = ['mon', 'tue', 'wed', 'thu', 'fri'];
    const weekend = ['sat', 'sun'];
    onChange({ ...channel, days: preset === 'all' ? all : preset === 'weekdays' ? weekdays : weekend });
  };

  const setPerDayField = (dayKey, field, value) => {
    onChange({
      ...channel,
      perDay: {
        ...channel.perDay,
        [dayKey]: {
          ...(channel.perDay[dayKey] || { enabled: true, start: '08:00', end: '18:00' }),
          [field]: value,
        },
      },
    });
  };

  return (
    <Card className={`p-4 transition-all ${channel.enabled ? '' : 'opacity-60'}`} data-testid={`resource-channel-editor-${channelKey}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: meta.bg }}>
            <Icon size={18} style={{ color: meta.color }} />
          </div>
          <div>
            <p className="font-secondary font-bold text-slate-900">{meta.label}</p>
            <p className="text-xs text-slate-500">{channel.enabled ? 'Ativo - configurar horário' : 'Inativo - nunca envia'}</p>
          </div>
        </div>
        <Switch checked={channel.enabled} onCheckedChange={value => onChange({ ...channel, enabled: value })} />
      </div>

      {channel.enabled && (
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Dias da semana</Label>
              <div className="flex gap-1">
                <button type="button" onClick={() => setDaysPreset('weekdays')} className="text-[10px] px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">Dias uteis</button>
                <button type="button" onClick={() => setDaysPreset('weekend')} className="text-[10px] px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">Fim-de-semana</button>
                <button type="button" onClick={() => setDaysPreset('all')} className="text-[10px] px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">Todos</button>
              </div>
            </div>
            <div className="flex gap-1.5">
              {DAYS.map(day => {
                const active = channel.days.includes(day.key);
                return (
                  <button
                    key={day.key}
                    type="button"
                    onClick={() => toggleDay(day.key)}
                    className={`flex-1 py-2 rounded-md text-xs font-semibold transition-all ${active ? 'text-white shadow-sm' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}
                    style={active ? { backgroundColor: meta.color } : {}}
                  >
                    {day.short}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-md">
            <Label className="text-xs text-slate-700 flex-1">Horario diferente por dia?</Label>
            <Switch checked={channel.mode === 'per-day'} onCheckedChange={value => onChange({ ...channel, mode: value ? 'per-day' : 'uniform' })} />
          </div>

          {channel.mode === 'uniform' ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Inicio</Label>
                <Input type="time" value={timeToHourMinute(channel.start)} onChange={event => onChange({ ...channel, start: timeToHourMinute(event.target.value) })} />
              </div>
              <div>
                <Label className="text-xs">Fim</Label>
                <Input type="time" value={timeToHourMinute(channel.end)} onChange={event => onChange({ ...channel, end: timeToHourMinute(event.target.value) })} />
              </div>
            </div>
          ) : (
            <div className="space-y-2 max-h-[240px] overflow-y-auto">
              {channel.days.map(dayKey => {
                const day = DAYS.find(current => current.key === dayKey);
                const perDay = channel.perDay[dayKey] || { enabled: true, start: '08:00', end: '18:00' };
                return (
                  <div key={dayKey} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                    <span className="text-xs font-semibold text-slate-700 w-16">{day?.label}</span>
                    <Switch checked={perDay.enabled} onCheckedChange={value => setPerDayField(dayKey, 'enabled', value)} />
                    {perDay.enabled && (
                      <>
                        <Input type="time" className="h-8 text-xs" value={timeToHourMinute(perDay.start)} onChange={event => setPerDayField(dayKey, 'start', timeToHourMinute(event.target.value))} />
                        <span className="text-xs text-slate-400">-</span>
                        <Input type="time" className="h-8 text-xs" value={timeToHourMinute(perDay.end)} onChange={event => setPerDayField(dayKey, 'end', timeToHourMinute(event.target.value))} />
                      </>
                    )}
                  </div>
                );
              })}
              {channel.days.length === 0 && <p className="text-xs text-slate-400 text-center py-3">Selecione primeiro os dias da semana.</p>}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const ChannelsSummary = ({ channels }) => (
  <div className="space-y-2">
    {Object.entries(normalizeChannels(channels)).map(([key, channel]) => {
      const meta = CHANNEL_META[key];
      const Icon = meta.Icon;

      return (
        <div key={key} className={`flex items-center gap-2 text-xs ${channel.enabled ? 'text-slate-700' : 'text-slate-400'}`}>
          <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: channel.enabled ? meta.bg : '#f1f5f9' }}>
            <Icon size={11} style={{ color: channel.enabled ? meta.color : '#94a3b8' }} />
          </div>
          {channel.enabled ? (
            <>
              <span className="font-medium w-14">{meta.label}</span>
              <Clock size={10} className="text-slate-400" />
              <span className="truncate">{formatDaysShort(channel.days)}</span>
              <span className="text-slate-400">-</span>
              <span className="font-mono">{channel.mode === 'per-day' ? 'variável' : `${timeToHourMinute(channel.start)}-${timeToHourMinute(channel.end)}`}</span>
            </>
          ) : (
            <>
              <span className="font-medium w-14">{meta.label}</span>
              <XCircle size={11} className="text-slate-300" />
              <span className="italic">Desativado</span>
            </>
          )}
        </div>
      );
    })}
  </div>
);

export const ContactRulesPage = () => {
  const { contacts: externalContacts, loading: externalContactsLoading, error: externalContactsError } = useExternalContacts();
  const { can } = usePermissions();
  const [ruleGroups, setRuleGroups] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [ruleGroupsLoading, setRuleGroupsLoading] = useState(true);
  const [ruleGroupsError, setRuleGroupsError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Form
  const [rgName, setRgName] = useState('');
  const [rgDesc, setRgDesc] = useState('');
  const [rgContacts, setRgContacts] = useState([]);
  const [rgChannels, setRgChannels] = useState({ sms: emptyChannel(), email: emptyChannel(), call: { ...emptyChannel(), enabled: false } });
  const [selectedContactId, setSelectedContactId] = useState('');

  const canReadRuleGroups = can(PERMISSIONS.CONTACT_RULE_GROUP_READ);
  const canCreateRuleGroups = can(PERMISSIONS.CONTACT_RULE_GROUP_CREATE);
  const canUpdateRuleGroups = can(PERMISSIONS.CONTACT_RULE_GROUP_UPDATE);
  const canDeleteRuleGroups = can(PERMISSIONS.CONTACT_RULE_GROUP_DELETE);
  const canReadContactRules = can(PERMISSIONS.CONTACT_RULE_READ);
  const canCreateContactRules = can(PERMISSIONS.CONTACT_RULE_CREATE);
  const canUpdateContactRules = can(PERMISSIONS.CONTACT_RULE_UPDATE);
  const canDeleteContactRules = can(PERMISSIONS.CONTACT_RULE_DELETE);

  const canReadPage = canReadRuleGroups && canReadContactRules;
  const canCreate = canCreateRuleGroups && canCreateContactRules;
  const canUpdate = canUpdateRuleGroups && canUpdateContactRules;
  const canDelete = canDeleteRuleGroups && canDeleteContactRules;

  useEffect(() => {
    if (!canReadPage) {
      setRuleGroups([]);
      setRuleGroupsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadRuleGroups = async () => {
      setRuleGroupsLoading(true);
      setRuleGroupsError('');

      try {
        const [ruleGroupsResponse, vehiclesResponse] = await Promise.allSettled([
          api.get(resourceContactRuleGroupEndpoints.list),
          fetchVehicles(),
        ]);

        if (ruleGroupsResponse.status === 'rejected') {
          throw ruleGroupsResponse.reason;
        }

        if (!cancelled) {
          setRuleGroups(normalizeRuleGroups(ruleGroupsResponse.value.data, externalContacts));
          setAssignments(vehiclesResponse.status === 'fulfilled' ? getVehicleAssignments(vehiclesResponse.value) : []);
        }
      } catch (error) {
        if (!cancelled) {
          setRuleGroups([]);
          setAssignments([]);
          setRuleGroupsError(getApiErrorMessage(error, 'Nao foi possível carregar os grupos de regras.'));
        }
      } finally {
        if (!cancelled) setRuleGroupsLoading(false);
      }
    };

    loadRuleGroups();

    return () => {
      cancelled = true;
    };
  }, [canReadPage, externalContacts]);

  const handleAddContact = () => {
    if (!selectedContactId) { toast.error('Selecione um contacto externo.'); return; }
    const contact = externalContacts.find(item => String(item.id) === String(selectedContactId));
    if (!contact) { toast.error('Selecione um contacto externo.'); return; }
    if (rgContacts.some(item => String(item.id) === String(contact.id))) {
      toast.error('Este contacto ja foi adicionado ao grupo.');
      return;
    }
    setRgContacts(previous => [...previous, normalizeExternalContact(contact)]);
    setSelectedContactId('');
  };
  const handleRemoveContact = (id) => setRgContacts(p => p.filter(c => c.id !== id));

  const handleOpenCreate = () => {
    if (!canCreate) {
      toast.error('Sem permissão para criar grupos e regras de contacto.');
      return;
    }
    setRgName(''); setRgDesc(''); setRgContacts([]); setRgChannels({ sms: emptyChannel(), email: emptyChannel(), call: { ...emptyChannel(), enabled: false } }); setSelectedContactId('');
    setIsCreateOpen(true);
  };
  const validateRuleGroup = () => {
    if (!rgName.trim()) { toast.error('Nome obrigatório.'); return; }
    if (rgContacts.length === 0) { toast.error('Adicione pelo menos um contacto.'); return; }
    for (const [key, channel] of Object.entries(rgChannels)) {
      if (channel.enabled && channel.days.length === 0) {
        toast.error(`${CHANNEL_META[key].label}: selecione pelo menos 1 dia.`);
        return;
      }
    }
    return true;
  };

  const handleCreate = async () => {
    if (!canCreate) {
      toast.error('Sem permissão para criar grupos e regras de contacto.');
      return;
    }
    if (!validateRuleGroup()) return;
    const payload = ruleGroupPayload({ name: rgName, description: rgDesc, contacts: rgContacts, channels: rgChannels });
    setSubmitting(true);
    try {
      const response = await api.post(resourceContactRuleGroupEndpoints.create, payload);
      const created = normalizeRuleGroup(response.data?.data ?? response.data?.group ?? response.data, externalContacts, {
        id: Math.max(...ruleGroups.map(r => r.id), 0) + 1,
        ...payload,
        contacts: rgContacts,
        channels: rgChannels,
      });
      setRuleGroups(p => [created, ...p]);
      setIsCreateOpen(false);
      toast.success(`Grupo "${created.name}" criado com sucesso!`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possível criar o grupo.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEdit = (rg) => {
    if (!canUpdate) {
      toast.error('Sem permissão para editar grupos e regras de contacto.');
      return;
    }
    setSelectedGroup(rg); setRgName(rg.name); setRgDesc(rg.description); setRgContacts([...rg.contacts]); setRgChannels(cloneChannels(rg.channels));
    setSelectedContactId('');
    setIsEditOpen(true);
  };
  const handleUpdate = async () => {
    if (!canUpdate) {
      toast.error('Sem permissão para editar grupos e regras de contacto.');
      return;
    }
    if (!rgName.trim() || rgContacts.length === 0) { toast.error('Nome e contactos obrigatórios.'); return; }
    for (const [key, channel] of Object.entries(rgChannels)) {
      if (channel.enabled && channel.days.length === 0) {
        toast.error(`${CHANNEL_META[key].label}: selecione pelo menos 1 dia.`);
        return;
      }
    }
    const payload = ruleGroupPayload({ name: rgName, description: rgDesc, contacts: rgContacts, channels: rgChannels });
    setSubmitting(true);
    try {
      const response = await api.post(resourceContactRuleGroupEndpoints.update, {
        id: selectedGroup.id,
        uuid: selectedGroup.uuid,
        ...payload,
      });
      const updated = normalizeRuleGroup(response.data?.data ?? response.data?.group ?? response.data, externalContacts, {
        ...selectedGroup,
        ...payload,
        contacts: rgContacts,
        channels: rgChannels,
      });
      setRuleGroups(p => p.map(r => r.id === selectedGroup.id ? updated : r));
      setIsEditOpen(false);
      toast.success('Grupo atualizado!');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possível atualizar o grupo.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (rgId) => {
    if (!canDelete) {
      toast.error('Sem permissão para eliminar grupos e regras de contacto.');
      return;
    }
    const group = ruleGroups.find(item => item.id === rgId);
    if (!group) return;
    if (!window.confirm(`Eliminar grupo "${group.name}"?`)) return;

    setSubmitting(true);
    try {
      await api.post(resourceContactRuleGroupEndpoints.delete, {
        id: group.id,
        uuid: group.uuid,
      });
      setRuleGroups(p => p.filter(r => r.id !== rgId));
      toast.success('Grupo removido.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possível remover o grupo.'));
    } finally {
      setSubmitting(false);
    }
  };

  const getAssignments = (rg) => {
    if (rg.assignments?.length) return rg.assignments;

    return assignments.filter(assignment =>
      String(assignment.ruleGroupId) === String(rg.id) ||
      assignment.ruleGroupIds?.some(id => String(id) === String(rg.id))
    );
  };
  const visibleAssignments = ruleGroups.flatMap(getAssignments);
  const uniqueAssignments = Array.from(
    new Map(visibleAssignments.map(assignment => [String(assignment.id ?? assignment.reference), assignment])).values()
  );
  const totalContacts = ruleGroups.reduce((acc, rg) => acc + rg.contacts.length, 0);
  const totalAssigned = uniqueAssignments.length;
  const totalActive = uniqueAssignments.filter(a => !a.hasDriver).length;

  const filteredGroups = ruleGroups.filter(rg =>
    rg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rg.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rg.contacts.some(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const renderContactForm = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Nome do Grupo *</Label><Input value={rgName} onChange={e => setRgName(e.target.value)} placeholder="Ex: Turno Diurno/Noturno" /></div>
        <div className="space-y-2"><Label>Descrição</Label><Input value={rgDesc} onChange={e => setRgDesc(e.target.value)} placeholder="Descrição breve" /></div>
      </div>
      {rgContacts.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Contactos ({rgContacts.length})</Label>
          {rgContacts.map(c => (
            <div key={c.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center"><span className="text-white font-bold text-xs">{c.name.split(' ').map(n => n[0]).join('')}</span></div>
                <div>
                  <p className="font-medium text-sm">{c.name}</p>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Phone size={10} />{c.phone}</span>
                    {c.email && <span className="flex items-center gap-1"><Mail size={10} />{c.email}</span>}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-red-500 h-8 w-8 p-0" onClick={() => handleRemoveContact(c.id)}><Trash2 size={14} /></Button>
            </div>
          ))}
        </div>
      )}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
        <Label className="text-sm font-semibold text-blue-900">Adicionar Contacto</Label>
        <div className="flex gap-2">
          <Select value={selectedContactId} onValueChange={setSelectedContactId} disabled={externalContactsLoading || externalContacts.length === 0}>
            <SelectTrigger className="bg-white">
              <SelectValue placeholder={externalContactsLoading ? 'A carregar contactos...' : 'Selecionar contacto externo'} />
            </SelectTrigger>
            <SelectContent>
              {externalContacts.map(contact => (
                <SelectItem key={contact.id} value={String(contact.id)}>
                  {contact.name} {contact.phone ? `- ${contact.phone}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" disabled={!selectedContactId || externalContactsLoading} onClick={handleAddContact}><Plus size={14} className="mr-1" />Adicionar</Button>
        </div>
        {externalContactsError && <p className="text-xs text-red-600">{externalContactsError}</p>}
        {!externalContactsLoading && externalContacts.length === 0 && !externalContactsError && (
          <p className="text-xs text-slate-500">Sem contactos disponiveis.</p>
        )}
      </div>
      <div className="space-y-3">
        <div>
          <Label className="text-sm font-semibold">Horario do grupo</Label>
          <p className="text-xs text-slate-500 mt-1">A agenda aplica-se ao grupo inteiro e aos seus canais de notificação.</p>
        </div>
        {['sms', 'email', 'call'].map(channelKey => (
          <ChannelEditor
            key={channelKey}
            channelKey={channelKey}
            channel={rgChannels[channelKey]}
            onChange={(channel) => setRgChannels(previous => ({ ...previous, [channelKey]: channel }))}
          />
        ))}
      </div>
    </div>
  );

  if (!canReadPage) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Card className="p-8 text-center max-w-md">
            <div className="w-14 h-14 rounded-2xl bg-red-100 mx-auto mb-4 flex items-center justify-center">
              <ShieldOff className="text-red-600" size={28} />
            </div>
            <h1 className="text-xl font-secondary font-bold text-slate-900">Acesso Negado</h1>
            <p className="text-sm text-slate-600 mt-2">
              Nao tem permissao para consultar grupos e regras de contacto.
            </p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Regras Tags sem responsável</h1>
            <p className="text-slate-600 font-primary mt-1">Grupos de contactos reutilizáveis para tags/veículos sem responsável atribuído</p>
          </div>
          {canCreate && (
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleOpenCreate} disabled={ruleGroupsLoading || externalContactsLoading || submitting}>
              <Plus size={18} className="mr-2" />Novo Grupo
            </Button>
          )}
        </div>

        {(ruleGroupsError || externalContactsError) && (
          <div className="space-y-3">
            {ruleGroupsError && <Card className="p-4 border-red-200 bg-red-50 text-sm text-red-700">{ruleGroupsError}</Card>}
            {externalContactsError && <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-700">{externalContactsError}</Card>}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2"><p className="text-slate-600 text-sm font-medium">Grupos</p><Bell className="text-blue-600" size={20} /></div>
            <p className="text-2xl font-secondary font-bold text-slate-900">{ruleGroups.length}</p>
            <p className="text-xs text-slate-500 mt-1">Criados</p>
          </Card>
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2"><p className="text-slate-600 text-sm font-medium">Contactos</p><Users className="text-emerald-600" size={20} /></div>
            <p className="text-2xl font-secondary font-bold text-slate-900">{totalContacts}</p>
            <p className="text-xs text-slate-500 mt-1">Total registados</p>
          </Card>
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2"><p className="text-slate-600 text-sm font-medium">Veículos Atribuídos</p><Car className="text-purple-600" size={20} /></div>
            <p className="text-2xl font-secondary font-bold text-slate-900">{totalAssigned}</p>
            <p className="text-xs text-slate-500 mt-1">Com regra associada</p>
          </Card>
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2"><p className="text-slate-600 text-sm font-medium">Regras Ativas</p><CheckCircle2 className="text-green-600" size={20} /></div>
            <p className="text-2xl font-secondary font-bold text-green-700">{totalActive}</p>
            <p className="text-xs text-slate-500 mt-1">Tags/veículos sem responsável</p>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input placeholder="Pesquisar grupos ou contactos..." className="pl-9" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>

        {/* Explanation */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Bell className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
          <div>
            <p className="font-medium text-blue-900">Como funciona</p>
            <p className="text-sm text-blue-700 mt-1">
              Crie grupos de contactos com horários. Atribua um grupo a cada veículo na página de <strong>Frota</strong>. 
              Quando o veículo <strong>não tem condutor</strong>, a regra fica <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>Ativa</span> e os contactos recebem notificações. 
              Quando atribui um condutor, a regra fica automaticamente <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400 inline-block"></span>Suspensa</span>.
            </p>
          </div>
        </div>

        {/* Rule Groups */}
        <div className="space-y-6">
          {ruleGroupsLoading && (
            <Card className="p-8 text-center text-slate-500">
              <Bell className="mx-auto mb-3 text-slate-300" size={48} />
              <p>A carregar grupos de regras...</p>
            </Card>
          )}
          {!ruleGroupsLoading && filteredGroups.map(rg => {
            const assigned = getAssignments(rg);
            const activeCount = assigned.filter(a => !a.hasDriver).length;
            return (
              <Card key={rg.id} className="overflow-hidden">
                <div className="p-5 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center"><Bell className="text-white" size={22} /></div>
                    <div>
                      <h3 className="font-secondary font-bold text-lg text-slate-900">{rg.name}</h3>
                      {rg.description && <p className="text-sm text-slate-600">{rg.description}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-blue-100 text-blue-700">{rg.contacts.length} contacto(s)</Badge>
                    {activeCount > 0 && <Badge className="bg-green-100 text-green-700">{activeCount} ativa(s)</Badge>}
                    {canUpdate && (
                      <Button variant="outline" size="sm" disabled={submitting} onClick={() => handleOpenEdit(rg)}><Edit size={14} className="mr-1" />Editar</Button>
                    )}
                    {canDelete && (
                      <Button variant="outline" size="sm" disabled={submitting} className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDelete(rg.id)}><Trash2 size={14} /></Button>
                    )}
                  </div>
                </div>
                <div className="p-5">
                  <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><Clock size={14} />Horario do grupo</p>
                    <ChannelsSummary channels={rg.channels} />
                  </div>

                  {/* Contacts Table */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Contacto</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>Email</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rg.contacts.map(c => (
                        <TableRow key={c.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center"><span className="text-white font-bold text-xs">{c.name.split(' ').map(n => n[0]).join('')}</span></div>
                              <span className="font-medium text-slate-900">{c.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">{c.phone}</TableCell>
                          <TableCell className="text-sm text-slate-600">{c.email || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Assigned Vehicles */}
                  {assigned.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><Car size={14} />Veículos com esta regra ({assigned.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {assigned.map(a => (
                          <div key={a.reference} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${!a.hasDriver ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
                            <div className={`w-2 h-2 rounded-full ${!a.hasDriver ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`} />
                            <span className="font-mono font-bold">{a.reference}</span>
                            <span className="text-slate-500">{a.name}</span>
                            <Badge className={`text-xs ${!a.hasDriver ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                              {!a.hasDriver ? 'Ativa' : 'Suspensa'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {assigned.length === 0 && (
                    <p className="mt-4 pt-4 border-t text-sm text-slate-400 italic flex items-center gap-2"><Unlink size={14} />Nenhum veículo com esta regra. Atribua na página de Frota.</p>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        {!ruleGroupsLoading && filteredGroups.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Bell className="mx-auto mb-3 text-slate-300" size={48} />
            <p className="font-medium">Nenhum grupo de regras encontrado</p>
            {canCreate && <p className="text-sm mt-1">Crie um novo grupo para começar</p>}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={isCreateOpen && canCreate} onOpenChange={(open) => setIsCreateOpen(open && canCreate)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Plus className="text-blue-600" size={20} />Novo Grupo de Regras</DialogTitle>
              <DialogDescription>Crie um grupo reutilizável de contactos para tags/veículos sem responsável atribuído</DialogDescription>
            </DialogHeader>
            {renderContactForm()}
            <DialogFooter>
              <Button variant="outline" disabled={submitting} onClick={() => setIsCreateOpen(false)}>Cancelar</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" disabled={submitting || !canCreate} onClick={handleCreate}><Plus size={16} className="mr-2" />{submitting ? 'A guardar...' : 'Criar Grupo'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditOpen && canUpdate} onOpenChange={(open) => setIsEditOpen(open && canUpdate)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Edit className="text-blue-600" size={20} />Editar Grupo</DialogTitle>
              <DialogDescription>Atualizar contactos do grupo {selectedGroup?.name}</DialogDescription>
            </DialogHeader>
            {renderContactForm()}
            <DialogFooter>
              <Button variant="outline" disabled={submitting} onClick={() => setIsEditOpen(false)}>Cancelar</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" disabled={submitting || !canUpdate} onClick={handleUpdate}>{submitting ? 'A guardar...' : 'Guardar'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
